// client/src/pages/sections/disengagementOptions.js
// Option vocabulary + validation for the shared client-disengagement follow-up
// form (see DisengagementFields.js). MIRRORS the server-side source of truth in
// api/shared/disengagement.js — keep the two in sync by hand (both are small).
// The backend re-validates every value, so this is UX only.

// "Did we terminate our services or did the client terminate our services?"
export const DISENGAGEMENT_TYPE_OPTIONS = [
  { value: 'bmss_terminated',   label: 'BMSS terminated services' },
  { value: 'client_terminated', label: 'Client terminated services' },
];

// Conditional single-select reasons per type (text matches the disengagement form).
export const REASONS_BY_TYPE = {
  bmss_terminated: [
    "BMSS does not work in the client's industry",
    'Poor client personality - treats BMSS badly',
    'Poor realization',
    'Orphan 1040',
    'Overly sensitive to fees',
    'Unpaid or bill not paid on time',
    'Unresponsive or uncooperative',
    'AR Dept. Use Only',
  ],
  client_terminated: [
    'Business sold or merged with another company',
    'Entity Dissolved',
    'Change of ownership',
    'Fees too high',
    'Not happy with the service',
    'The client has unreasonable expectations',
    'Other',
  ],
};

// Reasons that need a note explaining them (validated below; shown as required in the form).
export const REASONS_NEEDING_NOTES = ['Other'];

// "Programs which need to be separated from the firm." MULTI-select. value =
// stable code stored in the DB (comma-joined); label = what the form shows.
// 'None' is exclusive (see DisengagementFields). 'Multiple' retired — pick several.
export const PROGRAM_OPTIONS = [
  { value: 'QUICKBOOKS_ONLINE', label: 'QuickBooks Online' },
  { value: 'RIGHTWORKS',        label: 'Rightworks (formerly Right Networks)' },
  { value: 'BILL_COM',          label: 'Bill.com' },
  { value: 'UNSURE',            label: 'Unsure' },
  { value: 'NONE',              label: 'None' },
];
export const PROGRAM_NONE = 'NONE';

export const LETTER_DELIVERY_OPTIONS = [
  { value: 'paper', label: 'Paper' },
  { value: 'email', label: 'Email' },
];

// The empty starting state for a disengagement form.
// programsToSeparate is a multi-select array of program codes.
// relatedParties is the set of related clients (chips) selected to also disengage:
//   [{ clientCode, clientName, contIndex }]
export const EMPTY_DISENGAGEMENT = {
  disengagementType:         null,
  reasonCategory:            null,
  lostToFirm:                '',
  programsToSeparate:        [],
  disengagementLetterNeeded: null,
  letterDeliveryMethod:      null,
  notes:                     '',
  relatedParties:            [],
};

// ── Required-field config ──────────────────────────────────────────────────
// SINGLE place to control which answers are mandatory. Defaults mirror the
// asterisked fields on the original disengagement form (Todd wants low friction,
// so optional fields — firm we lost them to, estimated fee, notes — stay off).
// Flip any of these to false/true and validateDisengagement() follows.
export const DISENGAGEMENT_REQUIRED = {
  disengagementType:         true,
  reasonCategory:            true,   // only enforced once a type is chosen
  programsToSeparate:        true,   // at least one program (or None)
  disengagementLetterNeeded: true,
  letterDeliveryMethod:      true,   // only enforced when a letter IS needed
  lostToFirm:                false,
  notes:                     false,
};

// Returns a { field: message } map of validation errors (empty = valid).
export function validateDisengagement(v) {
  const val = v || EMPTY_DISENGAGEMENT;
  const req = DISENGAGEMENT_REQUIRED;
  const errors = {};

  if (req.disengagementType && !val.disengagementType) {
    errors.disengagementType = 'Select whether BMSS or the client terminated services.';
  }
  if (req.reasonCategory && val.disengagementType && !val.reasonCategory) {
    errors.reasonCategory = 'Select a reason.';
  }
  if (req.programsToSeparate && !(Array.isArray(val.programsToSeparate) && val.programsToSeparate.length)) {
    errors.programsToSeparate = 'Select which programs need to be separated.';
  }
  if (req.disengagementLetterNeeded && val.disengagementLetterNeeded !== true && val.disengagementLetterNeeded !== false) {
    errors.disengagementLetterNeeded = 'Indicate whether a disengagement letter is needed.';
  }
  if (req.letterDeliveryMethod && val.disengagementLetterNeeded === true && !val.letterDeliveryMethod) {
    errors.letterDeliveryMethod = 'Select a delivery method for the letter.';
  }
  if (req.lostToFirm && !String(val.lostToFirm || '').trim()) {
    errors.lostToFirm = 'Enter the firm we lost this client to.';
  }
  if (req.notes && !String(val.notes || '').trim()) {
    errors.notes = 'Enter notes.';
  } else if (REASONS_NEEDING_NOTES.includes(val.reasonCategory) && !String(val.notes || '').trim()) {
    errors.notes = `Add a note explaining the "${val.reasonCategory}" reason.`;
  }
  return errors;
}
